import React, { useState, useEffect, act } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Paper, Typography, TextField, Box, Select, MenuItem, InputLabel, FormControl, TableSortLabel, CircularProgress } from '@mui/material';
import { addActivityAsync, updateActivityAsync, selectActivity, clearSelectedActivity, 
  fetchActivities, fetchActivitiesListUsersFilterSrv, deleteActivityAsync } from '../app/activitiesSlice';
import Debug from '../utils/logger';
import { FormField } from 'semantic-ui-react';
import Button from '@msi/cobalt-react/button';
import Trash from '@msi/cobalt-react/icons/trash';
import Table from '@msi/cobalt-react/table';
import { useTheme } from '@mui/material/styles';
import { ToastContainer, addToast } from '@msi/cobalt-react/toast';

const debug = new Debug('PanelActivities');
debug.enabled = true;
debug.enableActions({'useEffect': true});

const PanelActivities = () => {
  const dispatch = useDispatch();
  const activities = useSelector((state) => state.activities.activities);
  const selectedActivity = useSelector((state) => state.activities.selectedActivity);
  const activitiesStatus = useSelector((state) => state.activities.status);
  const users = useSelector((state) => state.users.users);
  const scoring = useSelector((state) => state.scoring.scoring);
  const [formValues, setFormValues] = useState({ coreId: '', date: new Date().toISOString().slice(0, 10), activityId: '', hours: 0, comment: '', evidence: '' });
  const [showForm, setShowForm] = useState(false);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('id');
  const selectedRole = useSelector((state) => state.login.selectedRole);
  const coreId = useSelector((state) => state.login.coreId || '');
  const [calledForActivitiesTs, setCalledForActivitiesTs] = useState(0);
  const [activityAdded, setActivityAdded] = useState(false);
  const [hasInitialLoad, setHasInitialLoad] = useState(false); 
  const loggedInUserCoreId = useSelector((state) => state.login.coreId || '');

  const MinInterval = 1000;

  //Update the list of activities when needed
  useEffect(() => {
    debug.log('useEffect', "PanelActivities useEffect called");
    let nowTs = Date.now();
    let delta = (calledForActivitiesTs > 0) ? (nowTs - calledForActivitiesTs) : MinInterval;
    debug.log('useEffect', 'PanelActivities useEffect now, prev, delta:', nowTs, calledForActivitiesTs, delta);
    debug.log('useEffect', 'PanelActivities condition: ',((calledForActivitiesTs === 0) || (delta >= MinInterval)));
    if ((calledForActivitiesTs === 0) || (delta >= MinInterval)) {
      updateActivitiesByRole();
      setCalledForActivitiesTs(nowTs);
      if (!hasInitialLoad) {
        setHasInitialLoad(true);
      }
    }
    /* eslint-disable-next-line */
  }, [dispatch, selectedRole, users, coreId]);


  //Set the form values when an activity is selected
  useEffect(() => {
    if (selectedActivity) {
      setFormValues({ ...selectedActivity, date: new Date(selectedActivity.date).toISOString().slice(0, 10) });
      setShowForm(true);
    } else {
      setFormValues({ coreId: '', date: new Date().toISOString().slice(0, 10), activityId: '', hours: 0, comment: '', evidence: '' });  
      setShowForm(false);
    }
  }, [selectedActivity]);

  //Update the list of activites when one is modified
  useEffect(() => {
    if (activityAdded) {
      setActivityAdded(false);
      updateActivitiesByRole();
    }
  }, [activityAdded, activities, dispatch]);

  const updateActivitiesByRole = () => {
    if(selectedRole === 'User') {
      debug.log('useEffect', 'PanelActivities fetchActivities coreId:', coreId);
      if(coreId) {
        dispatch(fetchActivities(coreId));
      }
    } else if(selectedRole === 'Manager') {
      if(coreId) {
        let listCoreIds = [coreId];
        debug.log('useEffect', 'PanelActivities fetchActivitiesListUsers listCoreIds:', listCoreIds.length);
        dispatch(fetchActivitiesListUsersFilterSrv(listCoreIds));
      }
    } else {
      debug.log('useEffect', 'PanelActivities fetchActivities All');
      dispatch(fetchActivities());
    }
  }

  //Handle input changes in the form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    if(name === 'activityId') newValue = value.toUpperCase();
    if(name === 'coreId') newValue = value.toLowerCase();
    if(name === 'hours') {
      let val = parseFloat(value);
      if (val < 0) return; // Ensure hours cannot be negative
    }
    
    if (sortedUsers.length === 1)
      setFormValues({ ...formValues, coreId: sortedUsers[0].coreId.toLowerCase(), [name]: newValue });
    else
    setFormValues({ ...formValues, [name]: newValue });
  };

  const handleSubmit = async () => {

    if (!formValues.date || !formValues.coreId || !formValues.activityId || (formValues.activityId === 'OTHERACTIV' && formValues.comment === '') || (isActivityScoreMoreThan50(formValues.activityId, scoring) && formValues.evidence === '')) {
      alert('Please fill in all required fields.');
      return;
    }

    // Check if the date is before August 4th, 2025
    const selectedDate = new Date(formValues.date);
    const goLiveDate = new Date('2025-08-04');
    if (selectedDate < goLiveDate) {
      alert('Activities cannot be created before August 4th, 2025 (Go-Live date). Please select a valid date.');
      return;
    }
    if (!isActivityByHours(formValues.activityId)) {
      formValues.hours = 0;
    }

    if (isActivityByHours(formValues.activityId) && (formValues.hours === '' || formValues.hours <= 0)) {
      alert('Hours must be greater than 0');
      return;
    }

    if(isActivityScoreMoreThan50(formValues.activityId, scoring) && !formValues.evidence.startsWith('https://drive.google.com/')) {
      alert('Please provide a valid Google Drive link for evidence.');
      return;
    }

    //Close the form while the api processes the request
    setShowForm(false);

    try {
      if (selectedActivity) {
        await dispatch(updateActivityAsync({ ...formValues, id: selectedActivity.id, date: new Date(formValues.date) })).unwrap();
      } else {
        await dispatch(addActivityAsync({ ...formValues, date: new Date(formValues.date) })).unwrap();
      }
      setFormValues({ coreId: '', date: new Date().toISOString().slice(0, 10), activityId: '', hours: 0, comment: '', evidence: '' });
      dispatch(clearSelectedActivity());
      setActivityAdded(true);
      addToast('success', 'You\'ve submitted an activity!');
    } catch (error) {
      console.error('Failed to submit activity:', error);
      alert('Failed to submit activity. Please try again.');
    }
  };

  const handleCancel = () => {
    setFormValues({ coreId: '', date: new Date().toISOString().slice(0, 10), activityId: '', hours: 0, comment: '', evidence: '' });
    dispatch(clearSelectedActivity());
    setShowForm(false);
  };

  const handleDelete = async () => {
    if (selectedActivity) {
      setShowForm(false);
      await dispatch(deleteActivityAsync(selectedActivity.id)).unwrap();
      setFormValues({ coreId: '', date: new Date().toISOString().slice(0, 10), activityId: '', hours: 0, comment: '', evidence: '' });
      dispatch(clearSelectedActivity());
      setActivityAdded(true); // to re-fetch the activities after deletion  
    }
  };

  const handleRowClick = (activity) => {
    dispatch(selectActivity(activity));
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedActivities = activities.slice().sort((a, b) => {
    if (orderBy === 'date') {
      return order === 'asc' ? new Date(a[orderBy]) - new Date(b[orderBy]) : new Date(b[orderBy]) - new Date(a[orderBy]);
    } else if (orderBy === 'points') {
      const pointsA = scoring?.find(s => s.id === a.activityId)?.score || 0;
      const pointsB = scoring?.find(s => s.id === b.activityId)?.score || 0;
      const calculatedPointsA = isActivityByHours(a.activityId) ? a.hours * pointsA : pointsA;
      const calculatedPointsB = isActivityByHours(b.activityId) ? b.hours * pointsB : pointsB;
      return order === 'asc' ? calculatedPointsA - calculatedPointsB : calculatedPointsB - calculatedPointsA;
    } else {
      return order === 'asc' ? (a[orderBy] < b[orderBy] ? -1 : 1) : (a[orderBy] > b[orderBy] ? -1 : 1);
    }
  });

  const tableActivities = sortedActivities && sortedActivities.length;

  const rawUsers = (users && Array.isArray(users) && users.length) ? users : [];

  const sortedUsers = rawUsers ? [...rawUsers].sort((a, b) => {
    let aName = `${a.name}`;
    let aCoreId = `${a.coreId}`;
    let aCoreIdUpper = aCoreId.toLowerCase();
    let aString = `${aName} (${aCoreIdUpper})`;
    let bName = `${b.name}`;
    let bCoreId = `${b.coreId}`;
    let bCoreIdUpper = bCoreId.toLowerCase();
    let bString = `${bName} (${bCoreIdUpper})`;
    return aString.localeCompare(bString);
  }) : [];

  const showEvidenceField = (() => {
    const activity = scoring?.find(s => s.id === formValues.activityId);
    return activity && activity.score >= 50;
  })();

  return (
    <Box sx={{ backgroundColor: (theme) => theme.palette.secondary.main, color: (theme) => theme.palette.text.main, padding: 2 }}>
      <ToastContainer />
      <Typography variant="h4">Activities</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}></Box>
      <Typography variant="subtitle1" sx={{ mb: 2 }}>*Only activities completed on or after Go-Live (i.e. Aug 4 2025) will be counted towards your points.</Typography>
      {!showForm && activitiesStatus !== 'loading' && hasInitialLoad && <Button variant="contained" buttonType="primary" onClick={() => setShowForm(true)} size="large">Add New</Button>}
      {(activitiesStatus === 'loading' || !hasInitialLoad) && (
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={20} />
          <Typography>Loading activities...</Typography>
        </Box>
      )}
      {showForm && activitiesStatus !== 'loading' && hasInitialLoad && (
        <Box component="form" sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            <InputLabel shrink sx={{color: (theme) => theme.palette.text.main, px: 0.5, mt: 2 }}>User</InputLabel>
            <FormControl fullWidth margin="" size="small" sx={{ maxWidth: 500 }}>
              {sortedUsers.length > 1 ? (
                  <Select label="User" name="coreId" value={formValues.coreId.toLowerCase()} onChange={handleInputChange} disabled={selectedActivity ? true : false} sx={{ color: (theme) => theme.palette.text.main, mt: 1 }}>
                    {sortedUsers?.map((user) => (
                      <MenuItem key={user.coreId} value={user.coreId} sx={{ textAlign: 'left' }}>{`${user.name} (${user.coreId.toUpperCase()})`}</MenuItem>
                    ))}
                  </Select>
              ) : (
                <TextField
                  value={users.find(user => user.coreId === coreId)?.name || 'Unknown'}
                  fullWidth
                  margin=""
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{ mb: 1, maxWidth: 500, input: { color: (theme) => theme.palette.text.main }} }
                  InputProps={{
                    readOnly: true,
                  }}
                />
              )}
            </FormControl>
            <InputLabel shrink sx={{color: (theme) => theme.palette.text.main, px: 0.5, mt: 2 }}>Date</InputLabel>
            <TextField name="date" label="" type="date" value={formValues.date} onChange={handleInputChange} fullWidth InputLabelProps={{ shrink: true }} size="small"  sx={{ mb: 1, maxWidth: 500, label: { color: (theme) => theme.palette.text.main }, input: {color: (theme) => theme.palette.text.main} }} />
            <InputLabel shrink sx={{color: (theme) => theme.palette.text.main, px: 0.5, mt: 2 }}>Activity *</InputLabel>
            <FormControl fullWidth margin="" size="small" sx={{ maxWidth: 500 }}>
              <Select label="" name="activityId" value={formValues.activityId} onChange={handleInputChange} sx={{ color: (theme) => theme.palette.text.main }}>
                {scoring?.map((stage) => {
                  if (stage.enabled) return (
                    <MenuItem key={stage.id} value={stage.id} sx={{ textAlign: 'left' }}>{stage.stage}</MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            {isActivityByHours(formValues.activityId) && (
              <>
                <InputLabel shrink sx={{color: (theme) => theme.palette.text.main, px: 0.5, mt: 3 }}>Hours *</InputLabel>
                <TextField name="hours" label="" value={formValues.hours} onChange={handleInputChange} fullWidth margin="" size="small" slotProps={{ htmlInput: { sx: { color: (theme) => theme.palette.text.main }}}} sx={{ mb: 1, maxWidth: 500, label: { color: (theme) => theme.palette.text.main }, input: {color: (theme) => theme.palette.text.main} }} type="number" />
              </>
            )}
            {showEvidenceField && (
              <>
                <InputLabel shrink sx={{color: (theme) => theme.palette.text.main, px: 0.5, mt: 3 }}>Evidence Folder Link *</InputLabel>
                <Typography variant="subtitle2" sx={{ mb: 1, color: (theme) => theme.palette.text.secondary, width: '500px' }}>
                  Upload evidence (e.g., slides, videos, screenshots) of your completed activity to a Google Drive folder and paste the shared link in the textbox below. Make sure it’s accessible to others.
                </Typography>
                <TextField name="evidence" label="Add a Google Drive link" value={formValues.evidence} onChange={handleInputChange} fullWidth margin="" size="small" slotProps={{ htmlInput: { sx: { color: (theme) => theme.palette.text.main }}}} sx={{ mb: 1, maxWidth: 500, label: { color: (theme) => theme.palette.text.main }, input: {color: (theme) => theme.palette.text.main} }} error={!formValues.evidence.startsWith('https://drive.google.com/')} />
              </>
            )}
            <InputLabel shrink sx={{color: (theme) => theme.palette.text.main, px: 0.5, mt: 2 }}>{formValues.activityId === 'OTHERACTIV' ? 'Comment *' : 'Comment (Optional)'}</InputLabel>
            {
              formValues.activityId === 'OTHERACTIV' ? <TextField name="comment" label="" value={formValues.comment} onChange={handleInputChange} fullWidth margin="" size="small" slotProps={{ htmlInput: { sx: { color: (theme) => theme.palette.text.main }}}} sx={{ mb: 1, maxWidth: 500, label: { color: (theme) => theme.palette.text.main }, input: {color: (theme) => theme.palette.text.main} }} error={formValues.comment === ""} /> : <TextField name="comment" label="" value={formValues.comment} onChange={handleInputChange} fullWidth margin="" size="small" sx={{ mb: 1, maxWidth: 500, label: { color: (theme) => theme.palette.text.main }, input: {color: (theme) => theme.palette.text.main} }} />
            }
            <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row', maxWidth: '500px' }}>
              <Button variant="contained" buttonType="primary" disabled={formValues.activityId === '' || (formValues.activityId === 'OTHERACTIV' && formValues.comment === '')} onClick={handleSubmit} size="">{selectedActivity ? 'Update' : 'Submit'}</Button>
              <Button variant="contained" buttonType="secondary" onClick={handleCancel} size="">Cancel</Button>
              {selectedActivity && <Button variant="contained" buttonType="secondary" onClick={handleDelete} size=""><Trash />Delete</Button>}
            </Box>
          </Box>
        </Box>
      )}
      {(tableActivities && activitiesStatus !== 'loading' && hasInitialLoad && (
        <Box display='inline-block' sx={{ mt: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>{`*Click on a activity to update or delete it*`}</Typography>
          <Paper>
            <Table columns={[
              {
                field: 'id',
                header: 'Item ID',
                onSortClick: () => handleRequestSort(null, 'id'),
                sortDirection: orderBy === 'id' ? order : null
              },
              {
                field: 'userName',
                header: 'User Name',
                onSortClick: () => handleRequestSort(null, 'userName'),
                sortDirection: orderBy === 'userName' ? order : null
              },
              {
                field: 'coreId',
                header: 'Core ID',
                onSortClick: () => handleRequestSort(null, 'coreId'),
                sortDirection: orderBy === 'coreId' ? order : null
              },
              {
                field: 'date',
                header: 'Date',
                onSortClick: () => handleRequestSort(null, 'date'),
                sortDirection: orderBy === 'date' ? order : null
              },
              {
                field: 'activityId',
                header: 'Activity ID',
                onSortClick: () => handleRequestSort(null, 'activityId'),
                sortDirection: orderBy === 'activityId' ? order : null
              },
              {
                field: 'hours',
                header: 'Hours',
                onSortClick: () => handleRequestSort(null, 'hours'),
                sortDirection: orderBy === 'hours' ? order : null
              },
              {
                field: 'evidence',
                header: 'Evidence',
                onSortClick: () => handleRequestSort(null, 'evidence'),
                sortDirection: orderBy === 'evidence' ? order : null,
              },
              {
                field: 'comment',
                header: 'Comments',
                onSortClick: () => handleRequestSort(null, 'comment'),
                sortDirection: orderBy === 'comment' ? order : null
              },
              {
                field: 'points',
                header: 'Points',
                onSortClick: () => handleRequestSort(null, 'points'),
                sortDirection: orderBy === 'points' ? order : null
              }
            ]}
            tableContent={sortedActivities.map((activity) => {
              const user = rawUsers?.find(u => u.coreId === activity.coreId);
              let points = scoring?.find(s => s.id === activity.activityId)?.score || 0; // Default to 0 instead of 'N/A'
              if (isActivityByHours(activity.activityId)) {
                points = activity.hours * points;
              }
                return {
                id: activity.id,
                userName: user ? user.name : 'Unknown',
                coreId: activity.coreId?.toUpperCase() || '',
                date: new Date(activity.date).toLocaleDateString(),
                activityId: activity.activityId,
                hours: isActivityByHours(activity.activityId) ? activity.hours : 'N/A',
                evidence: activity.evidence
                  ? <a href={activity.evidence} target="_blank" rel="noopener noreferrer">Link</a>
                  : 'N/A',
                comment: activity.comment || 'N/A',
                points: points,
                };
            }).filter(activity => activity.coreId == loggedInUserCoreId.toUpperCase())}
            onRowClick={(event, rowKey) => {
              console.log("Row Key " + rowKey);
              handleRowClick(sortedActivities.find(activity => activity.id === rowKey));
            }}  
            >            
            </Table>
          </Paper>
        </Box>
      )) || (activitiesStatus !== 'loading' && hasInitialLoad && <Typography>No Activity Found.</Typography>)}
    </Box>
  );
};

const isActivityScoreMoreThan50 = (activityId, scoring) => {
  const activity = scoring?.find(s => s.id === activityId);
  return activity && activity.score >= 50;
}

export const isActivityByHours = (activityId) => ['AIRATTEND', 'AIRTRAIN', 'AIINTTRAIN', 'AIEXTTRAIN'].includes(activityId);

export default PanelActivities;
